import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, TrophyIcon, CalendarIcon, ClockIcon } from 'lucide-react';
interface LeaderboardProps {
  onBack: () => void;
  currentPlayers?: string[];
  currentScores?: Record<string, number[]>;
}
export function Leaderboard({
  onBack,
  currentPlayers,
  currentScores
}: LeaderboardProps) {
  const [filter, setFilter] = useState<'today' | 'week' | 'all'>('today');
  // Generate fake leaderboard data
  const generateFakeData = () => {
    const names = ['Alex M.', 'Sarah K.', 'James T.', 'Emma W.', 'Michael R.', 'Jessica L.', 'David B.', 'Ryan C.'];
    return names.map((name, i) => ({
      rank: i + 1,
      name,
      score: 18 + Math.floor(Math.random() * 15),
      time: `${Math.floor(Math.random() * 12) + 1}:${Math.floor(Math.random() * 50) + 10} ${Math.random() > 0.5 ? 'AM' : 'PM'}`,
      isCurrent: false
    })).sort((a, b) => a.score - b.score).map((item, i) => ({
      ...item,
      rank: i + 1
    }));
  };
  const [leaderboardData] = useState(generateFakeData());
  // If we have current players, inject them into the leaderboard for demo purposes
  let displayData = [...leaderboardData];
  if (currentPlayers && currentScores) {
    const currentTotals = currentPlayers.map(p => ({
      name: p,
      score: currentScores[p].reduce((a, b) => a + b, 0),
      time: 'Just now',
      isCurrent: true
    }));
    displayData = [...displayData, ...currentTotals].sort((a, b) => a.score - b.score).map((item, i) => ({
      ...item,
      rank: i + 1
    }));
  }
  return <div className="flex flex-col h-full bg-brand-charcoal" data-id="element-170">
      {/* Header */}
      <div className="bg-brand-green-deep pt-6 pb-4 px-6 rounded-b-3xl shadow-lg relative z-10" data-id="element-171">
        <div className="flex items-center justify-between mb-6" data-id="element-172">
          <button onClick={onBack} className="p-2 bg-brand-cream/10 rounded-full text-brand-cream-light hover:bg-brand-cream/20 transition-colors" data-id="element-173">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-174" />
          </button>
          <h2 className="text-xl font-heading text-brand-cream-light" data-id="element-175">
            Leaderboard
          </h2>
          <div className="w-9" data-id="element-176" /> {/* Spacer for centering */}
        </div>

        <div className="text-center mb-6" data-id="element-177">
          <span className="text-brand-gold text-xs font-bold uppercase tracking-widest block mb-1" data-id="element-178">
            The Sunset Green
          </span>
          <h3 className="text-3xl font-heading text-brand-cream-light" data-id="element-179">
            Course Records
          </h3>
        </div>

        {/* Filters */}
        <div className="flex bg-brand-charcoal/50 p-1 rounded-xl" data-id="element-180">
          {(['today', 'week', 'all'] as const).map(f => <button key={f} onClick={() => setFilter(f)} className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-lg transition-all ${filter === f ? 'bg-brand-gold text-brand-charcoal shadow-sm' : 'text-brand-cream/50 hover:text-brand-cream-light'}`} data-id="element-181">
              {f}
            </button>)}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-6 space-y-3" data-id="element-182">
        {displayData.map((entry, i) => <motion.div key={`${entry.name}-${i}`} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.05
      }} className={`flex items-center justify-between p-4 rounded-xl border ${entry.isCurrent ? 'bg-brand-gold/10 border-brand-gold/50' : i === 0 ? 'bg-brand-green-deep border-brand-gold/30' : 'bg-brand-cream/5 border-brand-cream/5'}`} data-id="element-183">
            <div className="flex items-center space-x-4" data-id="element-184">
              <div className={`w-8 text-center font-heading text-lg ${i === 0 ? 'text-brand-gold' : 'text-brand-cream/50'}`} data-id="element-185">
                {i === 0 ? <TrophyIcon className="w-5 h-5 mx-auto text-brand-gold" data-id="element-186" /> : entry.rank}
              </div>
              <div data-id="element-187">
                <h4 className={`font-heading text-lg ${entry.isCurrent ? 'text-brand-gold' : 'text-brand-cream-light'}`} data-id="element-188">
                  {entry.name} {entry.isCurrent && '(You)'}
                </h4>
                <div className="flex items-center text-[10px] text-brand-cream/40 uppercase tracking-wider mt-0.5 space-x-2" data-id="element-189">
                  <span className="flex items-center" data-id="element-190">
                    <ClockIcon className="w-3 h-3 mr-1" data-id="element-191" /> {entry.time}
                  </span>
                </div>
              </div>
            </div>
            <div className={`text-2xl font-heading ${entry.isCurrent || i === 0 ? 'text-brand-gold' : 'text-brand-cream-light'}`} data-id="element-192">
              {entry.score}
            </div>
          </motion.div>)}
      </div>
    </div>;
}